import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtMoney, errMsg } from "@/lib/format";
import { toast } from "sonner";
import { CalendarCheck, TrendingUp, TrendingDown, Check, Loader2, AlertTriangle, Wallet, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function ReconciliationPage() {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: rec }, { data: accs }] = await Promise.all([
        api.get(`/reconciliation/${month}`),
        api.get("/accounts"),
      ]);
      setData(rec);
      setAccounts(accs);
      setNotes(rec.closure?.notes || "");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const closeMonth = async () => {
    try {
      await api.post(`/reconciliation/${month}/close`, { notes });
      toast.success("Mes cerrado y archivado");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Build month options last 12 months
  const monthOpts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("es-DO", { month: "long", year: "numeric" });
    monthOpts.push({ key, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }

  return (
    <div className="space-y-6" data-testid="reconciliation-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Cierre de mes</div>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">Conciliación</h1>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button onClick={() => setImportOpen(true)} data-testid="open-import-csv"
            className="flex items-center justify-center gap-2 bg-white/5 hover:bg-violet-500/15 border border-white/10 px-3 py-2.5 rounded-xl text-sm flex-1 sm:flex-initial">
            <Upload className="w-4 h-4"/> <span className="sm:inline">CSV</span><span className="hidden sm:inline">&nbsp;bancario</span>
          </button>
          <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="month-select"
            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm flex-1 sm:flex-initial min-w-0">
            {monthOpts.map(m => <option key={m.key} value={m.key} className="bg-[#15141b]">{m.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="card-surface p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-violet-400 mx-auto"/></div>
      ) : data ? (
        <>
          {/* Top summary: 2 cols mobile, 3 cols desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <SummaryCard label="Ingresos" value={data.income} icon={TrendingUp} color="emerald" prefix="+"/>
            <SummaryCard label="Gastos" value={data.expense} icon={TrendingDown} color="rose" prefix="-"/>
            <div className="col-span-2 sm:col-span-1">
              <SummaryCard label="Balance neto" value={data.net} icon={Wallet} color={data.net >= 0 ? "emerald" : "rose"} prefix={data.net >= 0 ? "+" : ""}/>
            </div>
          </div>

          {/* Closure status */}
          {data.closure && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-4 flex items-center gap-3">
              <Check className="w-5 h-5 text-emerald-300"/>
              <div>
                <div className="text-sm font-semibold text-emerald-200">Mes cerrado</div>
                <div className="text-xs text-slate-400">Conciliado el {new Date(data.closure.closed_at).toLocaleString("es-DO")}</div>
              </div>
            </div>
          )}

          {/* Recurring status */}
          <Section title="Pagos recurrentes este mes">
            {data.recurring.length === 0 ? (
              <div className="text-sm text-slate-500">Sin pagos recurrentes configurados.</div>
            ) : (
              <div className="space-y-2">
                {data.recurring.map(r => (
                  <div key={r.id} className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2.5" data-testid={`recon-rec-${r.id}`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${r.is_paid ? "bg-emerald-400" : r.payments_count > 0 ? "bg-amber-400" : "bg-rose-400"}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.name}</div>
                      <div className="text-[10px] text-slate-500">{r.category} · {r.payments_count} pago(s)</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold">{fmtMoney(r.paid)} / {fmtMoney(r.expected)}</div>
                      <div className={`text-[10px] ${r.is_paid ? "text-emerald-300" : "text-rose-300"}`}>{r.is_paid ? "✓ Pagado" : `Faltan ${fmtMoney(r.remaining)}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Budgets */}
          <Section title="Presupuestos vs gastos">
            {data.budgets.length === 0 ? (
              <div className="text-sm text-slate-500">Sin presupuestos definidos.</div>
            ) : (
              <div className="space-y-2">
                {data.budgets.map((b, i) => {
                  const pct = b.limit > 0 ? Math.min(100, (b.spent / b.limit) * 100) : 0;
                  return (
                    <div key={i} className="bg-white/[0.03] border border-white/5 rounded-xl px-3 py-2.5">
                      <div className="flex items-center justify-between text-sm mb-1.5">
                        <span className="font-medium">{b.category}</span>
                        <span className={b.is_over ? "text-rose-300" : "text-slate-300"}>{fmtMoney(b.spent)} / {fmtMoney(b.limit)}</span>
                      </div>
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className={`h-full ${b.is_over ? "bg-rose-500" : pct >= 90 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Auto-debts */}
          {data.auto_debts && data.auto_debts.length > 0 && (
            <Section title={`Deudas generadas automáticamente (${data.auto_debts.length})`}>
              <div className="space-y-2">
                {data.auto_debts.map(d => (
                  <div key={d.id} className="flex items-center gap-3 bg-rose-500/5 border border-rose-500/20 rounded-xl px-3 py-2.5">
                    <AlertTriangle className="w-4 h-4 text-rose-300"/>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{d.name}</div>
                      <div className="text-[10px] text-slate-500">{d.creditor}</div>
                    </div>
                    <div className="text-sm font-semibold text-rose-300">{fmtMoney(d.balance)}</div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Notes + close */}
          <div className="card-surface p-5">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">Notas del cierre</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Observaciones del mes..." data-testid="closure-notes"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm resize-none"/>
            <button onClick={closeMonth} data-testid="close-month-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl shadow-[0_8px_30px_-10px_rgba(139,92,246,0.6)]">
              <CalendarCheck className="w-4 h-4"/> {data.closure ? "Re-cerrar mes" : "Cerrar mes y archivar"}
            </button>
          </div>
        </>
      ) : null}

      <ImportStatementDialog open={importOpen} onOpenChange={setImportOpen} accounts={accounts} onImported={load}/>
    </div>
  );
}

function ImportStatementDialog({ open, onOpenChange, accounts, onImported }) {
  const [file, setFile] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !accountId) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("account_id", accountId);
    setLoading(true);
    try {
      const { data } = await api.post("/statements/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.matched} conciliados, ${data.unmatched} sin coincidencia`);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setLoading(false); }
  };

  const importUnmatched = async () => {
    if (!result?.statement_id) return;
    try {
      const { data } = await api.post(`/statements/${result.statement_id}/import-unmatched`);
      toast.success(`${data.imported} transacciones importadas`);
      onImported?.(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { setFile(null); setResult(null); } onOpenChange(v); }}>
      <DialogContent className="bg-[#15141b] border-white/10 text-white sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="import-csv-dialog">
        <DialogHeader><DialogTitle className="text-white">Importar estado de cuenta</DialogTitle></DialogHeader>
        {!result ? (
          <form onSubmit={upload} className="space-y-3 mt-2">
            <Lbl>Cuenta destino</Lbl>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} required data-testid="import-account"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
              <option value="" className="bg-[#15141b]">Selecciona cuenta...</option>
              {accounts.map(a => <option key={a.id} value={a.id} className="bg-[#15141b]">{a.name}</option>)}
            </select>
            <Lbl>Archivo CSV o Excel del banco</Lbl>
            <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0])} required data-testid="import-file"
              className="w-full text-sm text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-violet-500/20 file:text-violet-200 file:text-sm"/>
            <p className="text-[10px] text-slate-500">El sistema detecta automáticamente las columnas (fecha, descripción, débito/crédito o monto).</p>
            <button type="submit" disabled={loading || !file} data-testid="import-submit"
              className="w-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-semibold py-3 rounded-xl disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto"/> : "Analizar y conciliar"}
            </button>
          </form>
        ) : (
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Stat label="Total" value={result.total_rows} color="text-white"/>
              <Stat label="Conciliados" value={result.matched} color="text-emerald-300"/>
              <Stat label="Sin match" value={result.unmatched} color="text-rose-300"/>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {result.rows.slice(0, 20).map((r, i) => (
                <div key={i} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg ${r.status === "matched" ? "bg-emerald-500/5" : "bg-rose-500/5"}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${r.status === "matched" ? "bg-emerald-400" : "bg-rose-400"}`}/>
                  <span className="text-slate-500 w-20">{r.date}</span>
                  <span className="flex-1 truncate">{r.description}</span>
                  <span className="font-mono font-semibold">{fmtMoney(r.amount)}</span>
                </div>
              ))}
            </div>
            {result.unmatched > 0 && (
              <button onClick={importUnmatched} data-testid="import-unmatched-btn"
                className="w-full bg-violet-500/20 border border-violet-500/40 text-violet-200 py-2.5 rounded-xl text-sm font-semibold">
                Importar las {result.unmatched} sin match
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }) {
  return (
    <section className="card-surface p-5">
      <h2 className="text-base font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function SummaryCard({ label, value, icon: Icon, color, prefix = "" }) {
  return (
    <div className={`p-4 rounded-2xl bg-${color}-500/15 border border-${color}-500/30`}>
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 text-${color}-300`}/>
        <span className={`text-[10px] uppercase tracking-wider font-semibold text-${color}-200`}>{label}</span>
      </div>
      <div className={`mt-2 text-xl font-bold text-${color}-300`}>{prefix}{fmtMoney(Math.abs(value))}</div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white/5 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-lg font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function Lbl({ children }) {
  return <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1.5">{children}</div>;
}
